import type { PublicStoreTrackingDto } from '@/api/generated';

const MEASUREMENT_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID ?? '').trim();
const GTM_CONTAINER_ID = (import.meta.env.VITE_GTM_CONTAINER_ID ?? '').trim();
const CONSENT_STORAGE_KEY = 'sala_cookie_consent';

export type ConsentStatus = 'accepted' | 'rejected';

interface FbqFunction {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: (...args: unknown[]) => void;
}

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
    fbq: FbqFunction;
    _fbq?: FbqFunction;
  }
}

type TrackingListener = () => void;

let bootstrapped = false;
let platformInitialized = false;
let consentGranted = false;
let storeDomain: string | null = null;
let storeSettings: PublicStoreTrackingDto | null = null;
let storeTrackersApplied = false;
let gtagJsLoadedFor: string | null = null;
let metaScriptLoaded = false;
let metaPixelInitialized: string | null = null;

const listeners = new Set<TrackingListener>();

export function subscribeTrackingConfig(listener: TrackingListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notifyTrackingConfig(): void {
  listeners.forEach((listener) => listener());
}

export function isPlatformAnalyticsConfigured(): boolean {
  return MEASUREMENT_ID.length > 0 || GTM_CONTAINER_ID.length > 0;
}

export function hasActiveStoreTracking(
  settings: PublicStoreTrackingDto | null = storeSettings
): boolean {
  if (!settings) {
    return false;
  }
  const metaOn = settings.meta.enabled && !!settings.meta.pixel_id;
  const ga4On = settings.ga4.enabled && !!settings.ga4.measurement_id;
  const adsOn = settings.google_ads.enabled && !!settings.google_ads.conversion_id;
  return metaOn || ga4On || adsOn;
}

/** Platform env IDs or per-store trackers from the API. */
export function isAnalyticsConfigured(): boolean {
  return isPlatformAnalyticsConfigured() || hasActiveStoreTracking();
}

/** Show cookie banner when tracking exists and user has not chosen yet. */
export function needsConsentBanner(): boolean {
  return getStoredConsent() === null && isAnalyticsConfigured();
}

export function isGtmConfigured(): boolean {
  return GTM_CONTAINER_ID.length > 0;
}

export function isGaConfigured(): boolean {
  return MEASUREMENT_ID.length > 0;
}

export function getStoredConsent(): ConsentStatus | null {
  try {
    const value = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (value === 'accepted' || value === 'rejected') {
      return value;
    }
  } catch {
    // localStorage may be unavailable
  }
  return null;
}

export function hasAnalyticsConsent(): boolean {
  return consentGranted;
}

function ensureGtag(): void {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
}

function setConsentDefaults(): void {
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    wait_for_update: 500,
  });
}

function ensureBootstrap(): void {
  if (bootstrapped) {
    return;
  }
  bootstrapped = true;
  ensureGtag();
  setConsentDefaults();
}

function loadGtm(containerId: string): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.setAttribute('style', 'display:none;visibility:hidden');
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function ensureGtagJs(primaryId: string): void {
  ensureGtag();
  if (gtagJsLoadedFor) {
    return;
  }
  gtagJsLoadedFor = primaryId;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${primaryId}`;
  document.head.appendChild(script);
  window.gtag('js', new Date());
}

function loadPlatformGa(measurementId: string): void {
  ensureGtagJs(measurementId);
  window.gtag('config', measurementId, {
    send_page_view: false,
  });
}

function normalizeAdsConversionId(conversionId: string): string {
  const trimmed = conversionId.trim();
  if (trimmed.toUpperCase().startsWith('AW-')) {
    return trimmed;
  }
  return `AW-${trimmed}`;
}

function ensureMetaScript(): void {
  if (metaScriptLoaded || typeof window.fbq === 'function') {
    metaScriptLoaded = true;
    return;
  }

  const fbq = function (...args: unknown[]) {
    const fn = window.fbq;
    if (fn.callMethod) {
      fn.callMethod(...args);
    } else {
      fn.queue = fn.queue || [];
      fn.queue.push(args);
    }
  } as FbqFunction;

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];

  window.fbq = fbq;
  if (!window._fbq) {
    window._fbq = fbq;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);
  metaScriptLoaded = true;
}

function applyMetaConsent(granted: boolean): void {
  if (typeof window.fbq !== 'function') {
    return;
  }
  window.fbq('consent', granted ? 'grant' : 'revoke');
}

function applyStoreTrackers(): void {
  if (!consentGranted || !storeSettings || storeTrackersApplied) {
    return;
  }

  const { meta, ga4 } = storeSettings;

  if (ga4.enabled && ga4.measurement_id) {
    const id = ga4.measurement_id.trim();
    ensureGtagJs(id);
    // Skip duplicate config if same as platform Measurement ID
    if (id !== MEASUREMENT_ID) {
      window.gtag('config', id, { send_page_view: false });
    }
  }

  if (meta.enabled && meta.pixel_id) {
    const pixelId = meta.pixel_id.trim();
    ensureMetaScript();
    applyMetaConsent(true);
    if (metaPixelInitialized !== pixelId) {
      window.fbq('init', pixelId);
      metaPixelInitialized = pixelId;
      window.fbq('track', 'PageView');
    }
  }

  storeTrackersApplied = true;
}

/**
 * Consent Mode defaults, then platform GTM and/or GA4.
 * Per-store trackers are applied via configureStoreTracking after the API responds.
 */
export function initAnalytics(): void {
  if (platformInitialized) {
    return;
  }

  if (!isPlatformAnalyticsConfigured()) {
    return;
  }

  platformInitialized = true;
  ensureBootstrap();

  if (GTM_CONTAINER_ID) {
    loadGtm(GTM_CONTAINER_ID);
  }

  if (MEASUREMENT_ID) {
    loadPlatformGa(MEASUREMENT_ID);
  }

  if (getStoredConsent() === 'accepted') {
    applyGrantedConsent(false);
  }
}

/** @deprecated Use initAnalytics */
export function initGA(): void {
  initAnalytics();
}

/**
 * Apply public store tracking settings from GET .../tracking.
 * Inits Meta / store GA4 after consent is granted. Google Ads loads on reservation only.
 */
export function configureStoreTracking(settings: PublicStoreTrackingDto): void {
  storeSettings = settings;
  storeTrackersApplied = false;
  metaPixelInitialized = null;

  if (hasActiveStoreTracking(settings)) {
    ensureBootstrap();
    if (getStoredConsent() === 'accepted' && !consentGranted) {
      applyGrantedConsent(false);
    } else if (consentGranted) {
      applyStoreTrackers();
    }
  }

  notifyTrackingConfig();
}

function applyGrantedConsent(persist: boolean): void {
  ensureBootstrap();
  if (persist) {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, 'accepted');
    } catch {
      // ignore
    }
  }
  consentGranted = true;
  window.gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
  if (storeDomain) {
    window.gtag('set', { store_domain: storeDomain });
  }
  applyStoreTrackers();
  notifyTrackingConfig();
}

export function grantConsent(): void {
  applyGrantedConsent(true);
}

export function denyConsent(): void {
  ensureBootstrap();
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, 'rejected');
  } catch {
    // ignore
  }
  consentGranted = false;
  storeTrackersApplied = false;
  window.gtag('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  if (metaScriptLoaded || typeof window.fbq === 'function') {
    applyMetaConsent(false);
  }
  notifyTrackingConfig();
}

export function setStoreDomain(domain: string | null): void {
  storeDomain = domain;
  if (!consentGranted || !domain) {
    return;
  }
  ensureGtag();
  window.gtag('set', { store_domain: domain });
  if (GTM_CONTAINER_ID) {
    window.dataLayer.push({ store_domain: domain });
  }
}

function eventParams(extra?: Record<string, string | number | boolean | undefined>) {
  return {
    ...extra,
    ...(storeDomain ? { store_domain: storeDomain } : {}),
  };
}

function shouldSendGoogleEvents(): boolean {
  return (
    MEASUREMENT_ID.length > 0 ||
    GTM_CONTAINER_ID.length > 0 ||
    !!(storeSettings?.ga4.enabled && storeSettings.ga4.measurement_id)
  );
}

function trackGoogleAdsConversion(): void {
  if (!consentGranted) {
    return;
  }

  const ads = storeSettings?.google_ads;
  if (!ads?.enabled || !ads.conversion_id || !ads.conversion_label) {
    return;
  }

  const awId = normalizeAdsConversionId(ads.conversion_id);
  ensureGtagJs(awId);
  window.gtag('config', awId);
  window.gtag('event', 'conversion', {
    send_to: `${awId}/${ads.conversion_label.trim()}`,
  });
}

export function trackPageView(path: string): void {
  if (!consentGranted || !isAnalyticsConfigured()) {
    return;
  }
  const params = eventParams({ page_path: path });
  if (shouldSendGoogleEvents()) {
    ensureGtag();
    window.gtag('event', 'page_view', params);
  }
  if (GTM_CONTAINER_ID) {
    window.dataLayer.push({ event: 'page_view', ...params });
  }
  if (
    storeSettings?.meta.enabled &&
    storeSettings.meta.pixel_id &&
    typeof window.fbq === 'function'
  ) {
    window.fbq('track', 'PageView');
  }
}

export function trackEvent(
  name: string,
  params?: Record<string, string | number | boolean | undefined>
): void {
  if (!consentGranted || !isAnalyticsConfigured()) {
    return;
  }
  const payload = eventParams(params);
  if (shouldSendGoogleEvents()) {
    ensureGtag();
    window.gtag('event', name, payload);
  }
  if (GTM_CONTAINER_ID) {
    window.dataLayer.push({ event: name, ...payload });
  }
}

/**
 * Reservation conversion: platform/store GA4 event, Meta Schedule, Google Ads conversion.
 */
export function trackReservationCompleted(): void {
  trackEvent('reservation_completed');

  if (!consentGranted) {
    return;
  }

  if (
    storeSettings?.meta.enabled &&
    storeSettings.meta.pixel_id &&
    typeof window.fbq === 'function'
  ) {
    window.fbq('track', 'Schedule');
  }

  trackGoogleAdsConversion();
}
